import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { deleteService, getServices } from '../../services/services';
import { onMutateSuccess, onQuerySuccess } from '../../utils/queryResponse';
import SetServiceModal from './servicesSet';
import { initColumns, rsdFormat, toLocalDateString } from '../../utils/helper';
import OverlayLoader from '../../components/overlayLoader';
import ConfirmationModal from '../../components/modals/confirmation';
import { SkeletonRows } from '../../components/skeleton';

const ServicesPage = () => {
    const { t } = useTranslation();

    const [setServiceModal, setSetServiceModal] = useState(false)
    const [services, setServices] = useState([])
    const [confirmationModal, setConfirmationModal] = useState(false)
    const [serviceToEdit, setServiceToEdit] = useState()
    const [serviceToDelete, setServiceToDelete] = useState()

    const { data, isFetching } = useQuery('services', getServices)
    const queryClient = useQueryClient()
    const { mutate, isLoading: isDeleting } = useMutation(deleteService, {
        onSuccess: onMutateSuccess(() => {
            queryClient.invalidateQueries(['services'])
        }, t('servicesPage.serviceDeleted'))
    })

    useEffect(() => {
        onQuerySuccess(data, (result) => {
            setServices(result.map(({ serviceCategory, ...rest }) => ({
                category: serviceCategory.name,
                ...rest,
            })))
        })
    }, [data])

    const handleShowSetServiceModal = (service) => { setServiceToEdit(service); setSetServiceModal(true) }
    const handleShowConfirmation    = (service) => { setConfirmationModal(true); setServiceToDelete(service) }
    const handleDeleteService       = () => mutate(serviceToDelete.id)

    /* Group services by category */
    const grouped = services.reduce((acc, svc) => {
        const cat = svc.category || t('servicesPage.uncategorised');
        if (!acc[cat]) acc[cat] = [];
        acc[cat].push(svc);
        return acc;
    }, {});

    return (
        <div className="sbr">
            <ConfirmationModal display={confirmationModal} toggle={setConfirmationModal} title={t('servicesPage.deleteTitle')}
                message={t('servicesPage.deleteMessage')} confirmAction={handleDeleteService}
                confirmText={t('common.confirm')} cancelText={t('common.cancel')} />
            <SetServiceModal display={setServiceModal} toggle={setSetServiceModal} serviceId={serviceToEdit?.id} />

            {/* Page header */}
            <div className="dhead">
                <div>
                    <div className="ttl">{t('servicesPage.title')}</div>
                    <div className="sub">{t('servicesPage.subtitle')}</div>
                </div>
                <div className="act">
                    <button className="btn-sbr btn-primary-sbr btn-sm-sbr" onClick={() => handleShowSetServiceModal()}>
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                        </svg>
                        {t('servicesPage.addService')}
                    </button>
                </div>
            </div>

            {/* Content */}
            {isFetching && services.length === 0 ? (
                <div className="card-sbr">
                    <SkeletonRows rows={6} widths={['16%', '24%', '16%', '16%', '14%']} />
                </div>
            ) : services.length === 0 ? (
                <div className="card-sbr">
                    <div className="sbr-empty">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M5 3v4M3 5h4M13 3l2.5 5L21 10l-5.5 2L13 17l-2.5-5L5 10l5.5-2z"/>
                        </svg>
                        {t('servicesPage.noServices')}
                    </div>
                </div>
            ) : (
                Object.entries(grouped).map(([category, catServices]) => (
                    <div key={category}>
                        {/* Category group header */}
                        <div className="daygroup">
                            <svg className="ic" style={{ width: '15px', height: '15px', color: 'var(--brand)' }}
                                viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 3v4M3 5h4M13 3l2.5 5L21 10l-5.5 2L13 17l-2.5-5L5 10l5.5-2z"/>
                            </svg>
                            <span className="d">{category}</span>
                            <span className="dot"></span>
                            <span className="meta">{t('servicesPage.service', { count: catServices.length })}</span>
                        </div>

                        {/* Service rows */}
                        <div className="card-sbr" style={{ padding: 0, overflow: 'hidden' }}>
                            <div style={{ padding: '2px 16px' }}>
                                {catServices.map((service, idx) => (
                                    <div key={service.id ?? idx} className="row-sbr">
                                        {/* Icon tile */}
                                        <div style={{
                                            width: '42px', height: '42px', borderRadius: '13px', flexShrink: 0,
                                            background: 'var(--brand-50)', border: '1px solid var(--brand-100)',
                                            color: 'var(--brand-ink)', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        }}>
                                            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M5 3v4M3 5h4M13 3l2.5 5L21 10l-5.5 2L13 17l-2.5-5L5 10l5.5-2z"/>
                                            </svg>
                                        </div>

                                        <div className="info">
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                                <span className="nm">{service.name}</span>
                                                {service.unit && (
                                                    <span style={{
                                                        fontSize: '10.5px', fontWeight: 800, letterSpacing: '.3px',
                                                        color: 'var(--muted)', background: 'var(--surface)',
                                                        border: '1px solid var(--line)', borderRadius: '7px', padding: '2px 8px',
                                                        flexShrink: 0,
                                                    }}>
                                                        {service.unit}
                                                    </span>
                                                )}
                                                {service.disabled && (
                                                    <span style={{
                                                        fontSize: '10.5px', fontWeight: 800, letterSpacing: '.3px',
                                                        color: 'var(--bad)', background: 'var(--bad-bg)',
                                                        border: '1px solid var(--bad-bd)', borderRadius: '7px', padding: '2px 8px',
                                                        flexShrink: 0,
                                                    }}>
                                                        {t('common.disabled')}
                                                    </span>
                                                )}
                                            </div>
                                            {service.pricePerUnit != null && (
                                                <div className="sub">{rsdFormat(service.pricePerUnit)} {t('servicesPage.perUnit', { unit: service.unit || t('common.unit') })}</div>
                                            )}
                                        </div>

                                        <div className="rt" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <button title={t('common.edit')} className="btn-ghost-sbr btn-sbr btn-sm-sbr"
                                                style={{ padding: '6px 8px', color: 'var(--brand)' }}
                                                onClick={() => handleShowSetServiceModal(service)}>
                                                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                                                </svg>
                                            </button>
                                            <button title={t('common.delete')} className="btn-ghost-sbr btn-sbr btn-sm-sbr"
                                                style={{ padding: '6px 8px', color: 'var(--bad)' }}
                                                onClick={() => handleShowConfirmation(service)}>
                                                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/>
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                ))
            )}

            {/* Overlay for background refresh */}
            {((isFetching && services.length > 0) || isDeleting) && (
                <div style={{ position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 10 }}>
                    <OverlayLoader />
                </div>
            )}
        </div>
    );
};

export default ServicesPage;
